import { s as cartStore } from './main-cart20261103-r1.js';

const PENDING_KEY = 'solstice.bf.pending-signup.v1';
const RECEIPT_KEY = 'solstice.bf.cart-linked.v1';
const TTL = 30 * 60 * 1000;
const IDENTITY_QUERY = 'mutation SignupCartIdentity($id:ID!,$identity:CartBuyerIdentityInput!){cartBuyerIdentityUpdate(cartId:$id,buyerIdentity:$identity){cart{id} userErrors{field message}}}';
const COUNTRY_QUERY = 'query SignupCartCountry($id:ID!){cart(id:$id){buyerIdentity{countryCode}}}';

export function initBfCartIdentity() {
  const configElement = document.getElementById('bf-cart-identity-config');
  if (!configElement || configElement.dataset.initialized) return;
  configElement.dataset.initialized = 'true';
  let config;
  try { config = JSON.parse(configElement.textContent); } catch { return; }
  const active = () => Date.now() >= config.startsAt && Date.now() < config.endsAt;
  const remove = key => { try { sessionStorage.removeItem(key); } catch {} };
  const read = key => {
    try {
      const value = JSON.parse(sessionStorage.getItem(key));
      if (!value || typeof value.createdAt !== 'number' || Date.now() - value.createdAt > TTL || value.createdAt > Date.now() + 1000) {
        remove(key);
        return null;
      }
      return value;
    } catch { remove(key); return null; }
  };
  const write = (key, value) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
      if (key === PENDING_KEY && value.email) setTimeout(() => {
        const current = read(PENDING_KEY);
        if (current?.createdAt === value.createdAt && current.phase === 'submitted') remove(PENDING_KEY);
      }, Math.max(0, TTL - (Date.now() - value.createdAt)));
      return true;
    } catch { return false; }
  };
  if (!active()) { remove(PENDING_KEY); remove(RECEIPT_KEY); return; }
  const formMatches = form => form instanceof HTMLFormElement && /^(BfSignup-|NewsletterForm-)/.test(form.id) && form.querySelector('[name="form_type"]')?.value === 'customer';
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!active() || !formMatches(form) || !form.checkValidity()) return;
    const input = form.querySelector('[name="contact[email]"]');
    const email = input?.value.trim();
    if (!email) return;
    remove(RECEIPT_KEY);
    write(PENDING_KEY, { formId: form.id, email, path: location.pathname, returnUrl: location.pathname + location.search + location.hash, createdAt: Date.now(), phase: 'submitted' });
  }, true);
  const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
  const request = async (url, options = {}) => {
    for (let attempt = 0; attempt < 2; attempt++) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 12000);
      let response;
      try { response = await fetch(url, { ...options, signal: controller.signal, cache: 'no-store' }); }
      finally { clearTimeout(timeout); }
      if (response.status === 429 && attempt === 0) {
        const seconds = Number(response.headers.get('Retry-After'));
        if (Number.isFinite(seconds) && seconds > 5) throw new Error('rate_limited');
        await delay(Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 2000);
        continue;
      }
      if (!response.ok) throw new Error('request_failed');
      return response.json();
    }
    throw new Error('request_failed');
  };
  const graphql = async (query, variables) => {
    const result = await request(config.endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query, variables }) });
    if (result.errors?.length || !result.data) throw new Error('request_failed');
    return result.data;
  };
  const cartUrl = suffix => new URL((window.Shopify?.routes?.root || '/') + suffix, location.origin).href;
  const waitForCart = async () => {
    for (let attempt = 0; attempt < 200; attempt++) {
      const mounted = document.getElementById('slide-out-cart')?.hasAttribute('data-v-app');
      if (mounted && !cartStore.getters.isUpdating && !cartStore.getters.items.some(item => item.updating)) return;
      await delay(50);
    }
    throw new Error('cart_busy');
  };
  const successRegions = [...document.querySelectorAll('[data-bf-identity-success]')];
  const pending = read(PENDING_KEY);
  const receipt = read(RECEIPT_KEY);
  if (pending?.email) write(PENDING_KEY, pending);
  const pendingRegion = successRegions.find(element => element.dataset.bfIdentitySuccess === pending?.formId && location.pathname === pending.path);
  if (pendingRegion && typeof pending.returnUrl === 'string') {
    try {
      const original = new URL(pending.returnUrl, location.origin);
      if (original.origin === location.origin && original.pathname === location.pathname) {
        const restored = new URL(location.href);
        for (const [key, value] of original.searchParams) {
          if (key !== 'customer_posted') restored.searchParams.set(key, value);
        }
        if (original.hash) restored.hash = original.hash;
        const nativeVariant = new URL(location.href).searchParams.get('variant');
        if (original.searchParams.has('variant') && original.searchParams.get('variant') !== nativeVariant) {
          if (!pending.variantRestored) {
            pending.variantRestored = true;
            if (write(PENDING_KEY, pending)) {
              location.replace(restored.pathname + restored.search + restored.hash);
              return;
            }
          }
          if (nativeVariant === null) restored.searchParams.delete('variant');
          else restored.searchParams.set('variant', nativeVariant);
        }
        history.replaceState(history.state, '', restored.pathname + restored.search + restored.hash);
      }
    } catch {}
  }
  const receiptRegion = successRegions.find(element => element.dataset.bfIdentitySuccess === receipt?.formId);
  const region = pendingRegion || receiptRegion || successRegions[0];
  if (!region) return;
  const message = region.querySelector('[data-bf-identity-message]');
  const retry = region.querySelector('[data-bf-identity-retry]');
  const form = region.closest('form');
  const viewCart = form?.querySelector('[data-bf-identity-view-cart]');
  const originalTabIndex = viewCart?.getAttribute('tabindex');
  let running = false;
  let record = pendingRegion ? pending : null;
  let originalCart = null;
  const show = (state, canRetry = false) => {
    region.hidden = false;
    region.dataset.bfIdentityState = state;
    message.textContent = config.messages[state];
    retry.hidden = !canRetry;
    retry.disabled = running;
    region.setAttribute('aria-busy', running ? 'true' : 'false');
    if (viewCart) {
      viewCart.setAttribute('aria-disabled', running ? 'true' : 'false');
      if (running) viewCart.setAttribute('tabindex', '-1');
      else if (originalTabIndex === null) viewCart.removeAttribute('tabindex');
      else viewCart.setAttribute('tabindex', originalTabIndex);
    }
  };
  viewCart?.addEventListener('click', event => {
    if (running) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  const hasSubscriberPricing = cart => cart.items.some(item => item.line_level_discount_allocations?.some(allocation => allocation.amount > 0 && allocation.discount_application?.title?.startsWith('Subscriber price: ')));
  const refresh = async () => {
    await waitForCart();
    const cart = await cartStore.dispatch('getItemsAsync');
    if (cartStore.getters.cartLoadError || !cart || !Array.isArray(cart.items) || typeof cart.total_price !== 'number') throw new Error('refresh_failed');
    return cart;
  };
  const run = async () => {
    if (running || !record || !active()) return;
    if (Date.now() - record.createdAt > TTL) { remove(PENDING_KEY); record = null; show('missing'); return; }
    running = true;
    show('applying');
    try {
      if (config.loggedIn) {
        delete record.email;
        record = { formId: record.formId, path: record.path, createdAt: record.createdAt, phase: 'refresh' };
        remove(PENDING_KEY);
        write(PENDING_KEY, record);
        await refresh();
        remove(PENDING_KEY);
        write(RECEIPT_KEY, { formId: record.formId, createdAt: Date.now(), state: 'signed_in' });
        record = null;
        running = false;
        show('signed_in');
        if (/^\/(?:[a-z]{2,3}(?:-[a-z0-9]{2,8})*\/)?cart\/?$/i.test(location.pathname)) location.reload();
        return;
      }
      if (record.phase === 'submitted') {
        await waitForCart();
        originalCart = await request(cartUrl('cart.js'));
        let cart = originalCart;
        if (cart.item_count === 0) cart = await request(cartUrl('cart/update.js'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates: {} }) });
        if (typeof cart.token !== 'string' || !cart.token) throw new Error('cart_missing');
        const id = 'gid://shopify/Cart/' + cart.token;
        const current = await graphql(COUNTRY_QUERY, { id });
        if (!current.cart) throw new Error('cart_missing');
        const identity = { email: record.email };
        if (current.cart.buyerIdentity.countryCode) identity.countryCode = current.cart.buyerIdentity.countryCode;
        const result = await graphql(IDENTITY_QUERY, { id, identity });
        if (result.cartBuyerIdentityUpdate.userErrors.length || !result.cartBuyerIdentityUpdate.cart) throw new Error('identity_failed');
        delete record.email;
        delete identity.email;
        record = { formId: record.formId, path: record.path, createdAt: record.createdAt, phase: 'refresh' };
        remove(PENDING_KEY);
        write(PENDING_KEY, record);
      }
      const cart = await refresh();
      const state = hasSubscriberPricing(cart) ? 'applied' : cart.item_count === 0 ? 'linked_empty' : 'linked';
      write(RECEIPT_KEY, { formId: record.formId, createdAt: Date.now(), state });
      if (state !== 'linked') remove(PENDING_KEY);
      running = false;
      show(state, state === 'linked');
      if (/^\/(?:[a-z]{2,3}(?:-[a-z0-9]{2,8})*\/)?cart\/?$/i.test(location.pathname) && originalCart && JSON.stringify(originalCart.items.map(item => item.final_line_price)) !== JSON.stringify(cart.items.map(item => item.final_line_price))) {
        remove(PENDING_KEY);
        location.reload();
      }
    } catch {
      running = false;
      show('error', true);
    }
  };
  retry.addEventListener('click', run);
  if (!record && receiptRegion) show(receipt.state);
  else if (!record) show('missing');
  else if (record?.phase === 'submitted' && typeof record.email !== 'string') { remove(PENDING_KEY); record = null; show('missing'); }
  else run();
}

initBfCartIdentity();
